/**
 * Tailscale Manager hooks — TanStack Query wrappers around our /tailscale
 * proxy endpoints. Phase 1 was read-only; Phase 3 adds mutations
 * (rename / expire / delete / tags / approve-routes / preauth CRUD).
 *
 * Polling cadences:
 *   * Nodes list:    10s (active section; many admins watch online/offline)
 *   * Other lists:   30s (lower-change; mutations invalidate anyway)
 *   * Detail:        10s (when sheet open)
 *
 * Mutations invalidate the matching query keys so the UI reflects
 * upstream changes immediately rather than waiting for the next poll.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
const BG = { refetchIntervalInBackground: false };
// ─── Nodes ─────────────────────────────────────────────────────────────
export function useTsNodes() {
    return useQuery({
        queryKey: ['ts', 'nodes'],
        queryFn: () => api('/tailscale/nodes'),
        refetchInterval: 10_000,
        ...BG,
    });
}
export function useTsNode(id) {
    return useQuery({
        queryKey: ['ts', 'nodes', id],
        queryFn: () => api(`/tailscale/nodes/${id}`),
        enabled: !!id,
        refetchInterval: 10_000,
        ...BG,
    });
}
// ─── Users ─────────────────────────────────────────────────────────────
export function useTsUsers() {
    return useQuery({
        queryKey: ['ts', 'users'],
        queryFn: () => api('/tailscale/users'),
        refetchInterval: 30_000,
        ...BG,
    });
}
// ─── Preauth keys ──────────────────────────────────────────────────────
// Headscale requires `?user=<numeric_id>` — list endpoint isn't global.
// We fetch per-user; for tailnets with many users the consumer page can
// fan out.
export function useTsPreauthKeys(userId) {
    return useQuery({
        queryKey: ['ts', 'preauth', userId],
        queryFn: () => api(`/tailscale/preauth-keys?user=${encodeURIComponent(userId ?? '')}`),
        enabled: !!userId,
        refetchInterval: 30_000,
        ...BG,
    });
}
// ─── ACL Policy ────────────────────────────────────────────────────────
export function useTsPolicy() {
    return useQuery({
        queryKey: ['ts', 'policy'],
        queryFn: () => api('/tailscale/policy'),
        refetchInterval: 30_000,
        ...BG,
    });
}
// ─── API keys ──────────────────────────────────────────────────────────
export function useTsApiKeys() {
    return useQuery({
        queryKey: ['ts', 'apikeys'],
        queryFn: () => api('/tailscale/apikeys'),
        refetchInterval: 60_000,
        ...BG,
    });
}
// ─── Routes (aggregated by backend) ────────────────────────────────────
export function useTsRoutes() {
    return useQuery({
        queryKey: ['ts', 'routes'],
        queryFn: () => api('/tailscale/routes'),
        refetchInterval: 30_000,
        ...BG,
    });
}
// ═════════════════════════════════════════════════════════════════════
// PHASE 3 — MUTATIONS
// ═════════════════════════════════════════════════════════════════════
// All wrap useMutation + invalidate the relevant query keys on success
// so the UI sees fresh data instantly. The backend's read-only gate
// short-circuits with 403 when `BACKUP_TAILSCALE_READ_ONLY=true`; the
// FE surfaces that error verbatim.
function invalidateNodeQueries(qc) {
    // Touch every node-related cache entry: full list (drives Dashboard
    // + Nodes page), routes view (derived from nodes), and any per-id
    // detail sheets currently open.
    qc.invalidateQueries({ queryKey: ['ts', 'nodes'] });
    qc.invalidateQueries({ queryKey: ['ts', 'routes'] });
}
// ─── Node mutations ──────────────────────────────────────────────────
export function useDeleteTsNode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (nodeId) => api(`/tailscale/nodes/${nodeId}`, { method: 'DELETE' }),
        onSuccess: () => invalidateNodeQueries(qc),
    });
}
export function useExpireTsNode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (nodeId) => api(`/tailscale/nodes/${nodeId}/expire`, { method: 'POST' }),
        onSuccess: () => invalidateNodeQueries(qc),
    });
}
export function useRenameTsNode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ nodeId, newName }) => api(`/tailscale/nodes/${nodeId}/rename`, {
            method: 'POST',
            body: { new_name: newName },
        }),
        onSuccess: () => invalidateNodeQueries(qc),
    });
}
export function useSetTsNodeTags() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ nodeId, tags }) => api(`/tailscale/nodes/${nodeId}/tags`, {
            method: 'POST',
            body: { tags },
        }),
        onSuccess: () => invalidateNodeQueries(qc),
    });
}
export function useApproveTsRoutes() {
    const qc = useQueryClient();
    return useMutation({
        // `routes` is the FULL set to keep approved (Headscale replaces, not
        // diff). The page that calls this is responsible for computing the
        // new set from the current state + the user's toggle.
        mutationFn: ({ nodeId, routes }) => api(`/tailscale/nodes/${nodeId}/approve-routes`, {
            method: 'POST',
            body: { routes },
        }),
        onSuccess: () => invalidateNodeQueries(qc),
    });
}
export function useCreateTsPreauth() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => api('/tailscale/preauth-keys', {
            method: 'POST',
            body,
        }),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['ts', 'preauth', vars.user] });
        },
    });
}
export function useExpireTsPreauth() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ user, key }) => api('/tailscale/preauth-keys/expire', {
            method: 'POST',
            body: { user, key },
        }),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['ts', 'preauth', vars.user] });
        },
    });
}
